import "./theme/global.css";
import React, { Suspense, lazy } from "react";
import App from "./App";
import { isAdminSubdomain } from "./utils/admin/adminSubdomain";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { isIgnorableClientRuntimeError } from "./utils/monitoring/ignorableClientErrors";

/** Admin portal only — keep out of the public entry graph (WN-116). */
const AdminPortalApp = lazy(() => import("./app/AdminPortalApp"));

// Global frontend error capture — sends unhandled errors to /api/client-error → Firestore systemEvents
function reportClientError(payload: { type: string; message: string; stack?: string; url?: string }) {
  if (isIgnorableClientRuntimeError(payload.message)) {
    return;
  }
  fetch("/api/client-error", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

window.onerror = (_message, _source, _lineno, _colno, error) => {
  const message = error?.message ?? String(_message);
  if (isIgnorableClientRuntimeError(message)) {
    return true;
  }
  reportClientError({
    type: "client.error",
    message,
    stack: error?.stack,
    url: window.location.href,
  });
  // Returning true above suppresses the browser's default handling for errors
  // we deliberately ignore. Everything else must still reach the console, which
  // an implicit undefined already did - this only says so explicitly, so the
  // handler has a return on every path and the type check can be enforced.
  return false;
};

window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  if (isIgnorableClientRuntimeError(message)) {
    event.preventDefault();
    return;
  }
  reportClientError({
    type: "client.unhandled-rejection",
    message,
    stack: reason instanceof Error ? reason.stack : undefined,
    url: window.location.href,
  });
};
import "./theme/radixThemeStyles";
import { customAccentColor } from "./theme/customTheme";
import { ThemeRoot } from "./theme/ThemeRoot";
import "./theme/wildNetworkTheme.css";
import "./theme/wnRoleTokens.css";
import "./theme/siteTheme.css";
import "./index.css";
import "../app/globals.css";
import ScrollToTop from "./components/ScrollToTop";
import { EventsFeatureProvider } from "./contexts/EventsFeatureContext";
import { ErrorBoundary } from "./components/monitoring/ErrorBoundary";

const style = document.createElement("style");
style.textContent = customAccentColor;
document.head.appendChild(style);

const container = document.getElementById("root");
if (container) {
  createRoot(container).render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <EventsFeatureProvider>
            <ThemeRoot>
              <ScrollToTop />
              {isAdminSubdomain() ? (
                <Suspense fallback={null}>
                  <AdminPortalApp />
                </Suspense>
              ) : (
                <App />
              )}
            </ThemeRoot>
          </EventsFeatureProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}
